import { useEffect, useRef, useState, type ReactElement } from "react";
import { Button, Modal } from "@perlab/ui";

export const SESSION_EXPIRY_WARNING_SECONDS = 60;
const WARNING_WINDOW_MS = SESSION_EXPIRY_WARNING_SECONDS * 1000;

export interface SessionExpiryDialogProps {
  deadlineMs: number | null;
  onContinue: () => Promise<void>;
  onExpired: () => void | Promise<void>;
  onSignOut: () => void;
}

/**
 * Global, deadline-based session warning. The displayed value is always derived
 * from the server-backed absolute deadline, so background-tab timer throttling
 * cannot extend or reset the session.
 */
export function SessionExpiryDialog({
  deadlineMs,
  onContinue,
  onExpired,
  onSignOut,
}: SessionExpiryDialogProps): ReactElement | null {
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [renewing, setRenewing] = useState(false);
  const [renewalError, setRenewalError] = useState<string | null>(null);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    setRenewalError(null);
  }, [deadlineMs]);

  useEffect(() => {
    if (deadlineMs === null) {
      setSecondsRemaining(null);
      return;
    }

    let warningTimer: number | null = null;
    let tickTimer: number | null = null;
    let expirationHandled = false;

    const clearWarningTimer = (): void => {
      if (warningTimer !== null) window.clearTimeout(warningTimer);
      warningTimer = null;
    };
    const clearTickTimer = (): void => {
      if (tickTimer !== null) window.clearInterval(tickTimer);
      tickTimer = null;
    };

    const syncToDeadline = (): void => {
      const remainingMs = deadlineMs - Date.now();
      if (remainingMs <= 0) {
        clearWarningTimer();
        clearTickTimer();
        setSecondsRemaining(0);
        if (!expirationHandled) {
          expirationHandled = true;
          void onExpiredRef.current();
        }
        return;
      }

      if (remainingMs > WARNING_WINDOW_MS) {
        setSecondsRemaining(null);
        clearTickTimer();
        clearWarningTimer();
        warningTimer = window.setTimeout(
          syncToDeadline,
          remainingMs - WARNING_WINDOW_MS,
        );
        return;
      }

      clearWarningTimer();
      setSecondsRemaining(Math.ceil(remainingMs / 1000));
      if (tickTimer === null) {
        tickTimer = window.setInterval(syncToDeadline, 250);
      }
    };

    const syncWhenVisible = (): void => {
      if (document.visibilityState === "visible") syncToDeadline();
    };

    syncToDeadline();
    window.addEventListener("focus", syncToDeadline);
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      clearWarningTimer();
      clearTickTimer();
      window.removeEventListener("focus", syncToDeadline);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [deadlineMs]);

  if (secondsRemaining === null) return null;
  const expired = secondsRemaining <= 0;

  const continueSession = async (): Promise<void> => {
    if (renewing || expired) return;
    setRenewalError(null);
    setRenewing(true);
    try {
      await onContinue();
    } catch {
      setRenewalError(
        "We could not renew your session. Check your connection and try again before the countdown ends.",
      );
    } finally {
      setRenewing(false);
    }
  };

  return (
    <Modal
      open
      dismissible={false}
      onClose={() => undefined}
      title="Your session is about to expire"
      ariaDescribedBy="session-expiry-description"
      footer={(
        <>
          <Button variant="secondary" onClick={onSignOut} disabled={renewing || expired}>
            Sign out now
          </Button>
          <Button
            autoFocus
            loading={renewing}
            disabled={expired}
            onClick={() => void continueSession()}
          >
            Continue session
          </Button>
        </>
      )}
    >
      <div id="session-expiry-description" className="space-y-3">
        {expired ? (
          <p role="status" className="text-[var(--color-text-secondary)]">
            Your session has expired. Returning you to sign in.
          </p>
        ) : (
          <>
            <p className="text-[var(--color-text-secondary)]">
              You will be signed out in{" "}
              <strong
                className="text-xl font-semibold tabular-nums text-[var(--color-accent)]"
                aria-label={`${secondsRemaining} seconds remaining`}
              >
                {secondsRemaining} seconds
              </strong>
              .
            </p>
            <p className="text-[var(--color-text-secondary)]">
              Select Continue session to keep your work open.
            </p>
          </>
        )}
        {renewalError && (
          <p role="alert" className="text-[var(--color-system-status-error)]">
            {renewalError}
          </p>
        )}
      </div>
    </Modal>
  );
}
